/**
 * @author Akash Mishra <akashmishra@eurofins.com>
 */
import { Injectable } from '@angular/core';
import { AuthenticationService } from '../authentication/authentication.service';
import { Idle } from '@ng-idle/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'src/environments/environment';
import { NotificationService } from 'src/app/shared/services/notification-service';
import { LoginResponse } from '../../interfaces/login-response';
import { SessionMessage } from 'src/app/shared/error-messages';
import { NgZone } from "@angular/core";
const MAT_SIMPLE_SNACKBAR = 'mat-simple-snackbar';

@Injectable({
  providedIn: 'root'
})

export class SessionManagementService {

  snackBar: MatSnackBar;
  idleTime: Idle;
  bodyElement: HTMLElement;
  isSessionGoingToExpire = false;
  timeOutShow: number;
  checkInterval;
  isRefreshTokenCalled = false;

  constructor(private readonly authenticationService: AuthenticationService,
    private readonly notification: NotificationService,
    private readonly idle: Idle,
    private readonly ngZone: NgZone) {
    this.idleTime = idle;
    this.bodyElement = document.body;
    this.snackBar = this.notification.getSnackBar();
    this.timeOutShow = this.authenticationService.tokenTimeout();
  }

  lastAction(value) {
    localStorage.setItem('userlastAction', JSON.stringify(value))
  }

  getLastAction() {
    return localStorage.getItem('userlastAction');
  }

  initListener() {
    this.bodyElement.addEventListener('click', this.reset);
    this.bodyElement.addEventListener('mousemove', this.reset);
    this.bodyElement.addEventListener('keydown', this.reset);
  }

  reset = () => {
    if (this.authenticationService.GetClaims()) {
      this.lastAction(Date.now());
      this.dismiss();
    } else {
      this.bodyElement.removeEventListener('click', this.reset);
      this.bodyElement.removeEventListener('mousemove', this.reset);
      this.bodyElement.removeEventListener('keydown', this.reset);
    }
  }

  check() {
    const now = Date.now();
    const timeOut = parseInt((environment.timeOut).toString(), 10) * 60 * 1000;
    const timeLeft = parseInt(this.getLastAction()) + timeOut;
    const diff = timeLeft - now;
    const isTimeout = diff < 0;

    if (!isTimeout && diff <= 300000) {
      const date = new Date(diff).toISOString().substr(14, 5);
      if (document.getElementsByClassName(MAT_SIMPLE_SNACKBAR)[0]) {
        let msg = SessionMessage.SNACKBAR_LOGOFF_TIMER_MESSAGE;
        msg = msg.replace('X', date.toString());
        document.getElementsByClassName(MAT_SIMPLE_SNACKBAR)[0].children[0].innerHTML
          = `${msg}`;
      }
      else {
        let msg = SessionMessage.SNACKBAR_LOGOFF_TIMER_MESSAGE;
        msg = msg.replace('X', date.toString());
        this.notification.sessionWarningAutoLogoff(msg);
      }
    }
    else if (document.getElementsByClassName(MAT_SIMPLE_SNACKBAR)[0]) {
      this.notification.sessionWarningAutoLogoffDismiss();
    }
    this.ngZone.run(() => {
      if (isTimeout && this.authenticationService.GetClaims()) {
        this.logout();
      }
    });
  }

  setupSessionLogoutSnackbar() {
    this.initListener();
    this.lastAction(Date.now());
    this.checkInterval = setInterval(() => {
      this.check();
    }, 1000)
    this.setupSessionSnackbar();
  }

  setupSessionSnackbar() {
    if (this.authenticationService.GetClaims()) {
      const tokenTime = this.authenticationService.tokenTimeout();
      const idleTime = tokenTime;
      this.idleTime.setIdle(idleTime - 3 * 60);
      this.idleTime.setTimeout(3 * 60);

      this.idleTime.onInterrupt.subscribe(() => {
        if (this.authenticationService.tokenTimeout() > 300) {
          this.dismiss();
        }
        this.setupSessionSnackbar();
      });

      this.idleTime.watch();

      this.idleTime.onTimeout.subscribe(() => {
        this.logout();
      });

      this.idleTime.onIdleStart.subscribe(() => {

        if (new Date(new Date(0).setUTCSeconds(
          this.authenticationService.GetClaims().exp).valueOf() - new Date().getUTCSeconds()) > new Date()
        ) {
          this.refreshToken();
        } else {
          this.logout();
        }
      });
    }
  }

  dismiss() {
    this.notification.sessionWarningAutoLogoffDismiss();
  }

  logout() {
    localStorage.removeItem('userlastAction');
    clearInterval(this.checkInterval);
    setTimeout(() => {
      let msg = SessionMessage.SNACKBAR_AUTO_LOGOFF_MESSAGE;
      msg = msg.replace('X', (parseInt((environment.timeOut).toString(), 10)).toString());
      const ref = this.notification.sessionWarning(msg, 'Dismiss');
      ref.onAction().subscribe(() => {
        this.dismiss();
      });
    }, 1000);
    this.authenticationService.Logout();
    this.reset();
  }

  refreshToken() {
    if (!this.isRefreshTokenCalled) {
      this.isRefreshTokenCalled = true;
      if (new Date(new Date(0).setUTCSeconds(this.authenticationService.GetClaims().exp)
        .valueOf() - new Date().getUTCSeconds()) > new Date()) {
        this.authenticationService.refreshToken().subscribe((data: LoginResponse) => {
          this.authenticationService.SetAuthInfo(data);
          this.authenticationService.SetClaims();
          this.dismiss();
          this.setupSessionSnackbar();
          this.isRefreshTokenCalled = false;
        });
      }
    }
  }
}
